import { useEffect, useState } from 'react';
import {
  addSubscriptionWebhook,
  ApiError,
  fetchSubscription,
  removeSubscriptionWebhook,
  saveSubscription,
  testSubscriptionWebhook,
  type SubscriptionState,
  type WatchWebhook,
} from '../api';
import { formatDateTime } from '../lib/format';
import type { AggregateBundle, AreaId, Meta } from '../types';

function deliverySummary(email: boolean, channels: number): string {
  const targets = [
    ...(email ? ['mejl'] : []),
    ...(channels > 0 ? [`${channels} ${channels === 1 ? 'Teams-kanal' : 'Teams-kanaler'}`] : []),
  ];
  return targets.length === 0 ? 'ingen mottagare vald' : targets.join(' och ');
}

/* Egna ikoner i stället för Teams-loggan: den är ett varumärke, och en neutral
   pratbubbla säger ändå vad raden handlar om. */
function MailIcon() {
  return (
    <svg className="channel__icon" viewBox="0 0 20 20" aria-hidden="true">
      <rect x="2" y="4" width="16" height="12" rx="2" />
      <path d="M3 6l7 5 7-5" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg className="channel__icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M3 5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H8l-4 4v-4H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

/** Väljs ett land gäller bevakningen varje ort i landet, inte bara annonser märkta med landsnamnet. */
const COUNTRIES = new Set(['Sverige', 'Danmark', 'Norge', 'Finland']);

interface SubscriptionViewProps {
  meta: Meta | undefined;
  stats: AggregateBundle;
}

interface AreaTechPickerProps {
  areaName: string;
  chosen: string[];
  /** Områdets vanligaste tekniker, med antal. */
  options: { name: string; count: number }[];
  /** Alla tekniker i datan, för att nå dem som inte ligger i topplistan. */
  allTechnologies: string[];
  onToggle: (tech: string) => void;
  onClear: () => void;
}

/**
 * Topplistan räcker för de flesta val, men inte för den som är ute efter något
 * smalt. Sökrutan når hela teknikregistret så att ett sällsynt val inte kräver
 * att tekniken råkar ligga bland områdets vanligaste.
 */
function AreaTechPicker({ areaName, chosen, options, allTechnologies, onToggle, onClear }: AreaTechPickerProps) {
  const [query, setQuery] = useState('');

  const shown = [
    ...options,
    // En vald teknik utanför topplistan måste synas, annars går den inte att välja bort.
    ...chosen.filter((tech) => !options.some((option) => option.name === tech)).map((name) => ({ name, count: 0 })),
  ];

  const search = query.trim().toLowerCase();
  const suggestions = search
    ? allTechnologies
        .filter((tech) => tech.toLowerCase().includes(search) && !shown.some((option) => option.name === tech))
        .slice(0, 8)
    : [];

  return (
    <div className="watch-tech">
      <div className="watch-tech__head">
        <strong>{areaName}</strong>
        <span className="panel__tools">
          {chosen.length === 0 ? 'allt inom området' : `${chosen.length} ${chosen.length === 1 ? 'teknik' : 'tekniker'}`}
        </span>
        {chosen.length > 0 ? (
          <button type="button" className="link-button" onClick={onClear}>
            Rensa
          </button>
        ) : null}
      </div>

      <div className="place-grid">
        {shown.map((option) => {
          const on = chosen.includes(option.name);
          return (
            <button
              key={option.name}
              type="button"
              className={`tag tag--tech${on ? ' tag--on' : ''}`}
              aria-pressed={on}
              onClick={() => onToggle(option.name)}
            >
              {option.name} <span className="tag__count">{option.count}</span>
            </button>
          );
        })}
      </div>

      <input
        className="control control--search"
        type="search"
        placeholder={`Sök teknik i ${areaName.toLowerCase()}…`}
        value={query}
        autoComplete="off"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && suggestions[0]) {
            event.preventDefault();
            onToggle(suggestions[0]);
            setQuery('');
          }
        }}
      />
      {suggestions.length > 0 ? (
        <div className="chip-row" style={{ marginTop: 6 }}>
          {suggestions.map((tech) => (
            <button
              key={tech}
              type="button"
              className="tag tag--tech"
              onClick={() => {
                onToggle(tech);
                setQuery('');
              }}
            >
              {tech}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Bevakning: välj områden, eventuellt orter, och få nya uppdrag i mejlen så
 * fort insamlingen hittat dem. Inget annat filtrerar – aldrig källa, år eller
 * teknik – så siffrorna här räknas alltid på all data, oavsett vad som är
 * filtrerat i de andra flikarna.
 */
export function SubscriptionView({ meta, stats }: SubscriptionViewProps) {
  const [state, setState] = useState<SubscriptionState>();
  const [selected, setSelected] = useState<Set<AreaId>>(new Set());
  const [places, setPlaces] = useState<Set<string>>(new Set());
  const [techs, setTechs] = useState<Partial<Record<AreaId, string[]>>>({});
  const [includeWithoutLocation, setIncludeWithoutLocation] = useState(false);
  const [includeWithoutTechnologies, setIncludeWithoutTechnologies] = useState(false);
  const [webhooks, setWebhooks] = useState<WatchWebhook[]>([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookLabel, setWebhookLabel] = useState('');
  const [webhookBusy, setWebhookBusy] = useState<string>();
  const [webhookNote, setWebhookNote] = useState<{ ok: boolean; message: string }>();
  const [showGuide, setShowGuide] = useState(false);
  const [guideFor, setGuideFor] = useState<'teams' | 'discord'>('teams');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSubscription(controller.signal)
      .then((result) => {
        setState(result);
        setSelected(new Set(result.areas));
        setPlaces(new Set(result.locations ?? []));
        setTechs(result.technologies ?? {});
        setIncludeWithoutLocation(result.includeWithoutLocation ?? false);
        setIncludeWithoutTechnologies(result.includeWithoutTechnologies ?? false);
        setWebhooks(result.webhooks ?? []);
        setEmailEnabled(result.emailEnabled ?? true);
        setNotifyEmail(result.notifyEmail ?? result.email);
      })
      .catch((cause: Error) => {
        if (cause.name !== 'AbortError') setError(cause.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const toggle = (areaId: AreaId) => {
    setSaved(false);
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const toggleTech = (areaId: AreaId, tech: string) => {
    setSaved(false);
    setTechs((current) => {
      const chosen = current[areaId] ?? [];
      const next = chosen.includes(tech) ? chosen.filter((name) => name !== tech) : [...chosen, tech];
      return { ...current, [areaId]: next };
    });
  };

  const clearTechs = (areaId: AreaId) => {
    setSaved(false);
    setTechs((current) => ({ ...current, [areaId]: [] }));
  };

  const togglePlace = (name: string) => {
    setSaved(false);
    setPlaces((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      const result = await saveSubscription([...selected], [...places], techs, {
        includeWithoutLocation,
        includeWithoutTechnologies,
        emailEnabled,
        notifyEmail: notifyEmail.trim(),
      });
      setState(result);
      setNotifyEmail(result.notifyEmail ?? result.email);
      setSaved(true);
      setWebhooks(result.webhooks ?? []);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Bevakningen kunde inte sparas.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="skeleton" style={{ height: 260 }} />;

  const areas = meta?.areas ?? [];
  const sources = meta?.sources ?? [];
  const allTechnologies = stats.technologies.map((tech) => tech.name);
  const narrowedAreas = [...selected].filter((areaId) => (techs[areaId] ?? []).length > 0).length;
  const countFor = (areaId: string) => stats.areas.find((area) => area.id === areaId)?.count ?? 0;
  // En vald ort kan ha fallit ur datan sedan den valdes. Den ska ändå synas,
  // annars går den inte att kryssa ur.
  const locationOptions = [
    ...stats.topLocations,
    ...[...places]
      .filter((name) => !stats.topLocations.some((place) => place.name === name))
      .map((name) => ({ name, count: 0 })),
  ];
  // En avstängd bevakning ska gå att spara om utan att först ändra valen –
  // annars finns inget sätt att starta den igen.
  const sameTechs = (areaId: AreaId) => {
    const before = state?.technologies?.[areaId] ?? [];
    const now = techs[areaId] ?? [];
    return before.length === now.length && before.every((tech) => now.includes(tech));
  };
  const addChannel = async () => {
    setWebhookBusy('add');
    setWebhookNote(undefined);
    try {
      const result = await addSubscriptionWebhook(webhookUrl.trim(), webhookLabel.trim());
      setWebhooks(result.webhooks);
      setWebhookUrl('');
      setWebhookLabel('');
      setWebhookNote({ ok: true, message: 'Kanalen är tillagd. Skicka ett testpling för att se att den fungerar.' });
    } catch (cause) {
      setWebhookNote({
        ok: false,
        message: cause instanceof ApiError ? cause.message : 'Kanalen kunde inte sparas.',
      });
    } finally {
      setWebhookBusy(undefined);
    }
  };

  const removeChannel = async (webhook: WatchWebhook) => {
    setWebhookBusy(webhook.id);
    setWebhookNote(undefined);
    try {
      const result = await removeSubscriptionWebhook(webhook.id);
      setWebhooks(result.webhooks);
      setWebhookNote({ ok: true, message: `${webhook.label} är borttagen.` });
    } catch (cause) {
      setWebhookNote({
        ok: false,
        message: cause instanceof ApiError ? cause.message : 'Kanalen kunde inte tas bort.',
      });
    } finally {
      setWebhookBusy(undefined);
    }
  };

  const testChannel = async (webhook: WatchWebhook) => {
    setWebhookBusy(webhook.id);
    setWebhookNote(undefined);
    try {
      await testSubscriptionWebhook(webhook.id);
      setWebhookNote({ ok: true, message: `Testpling skickat till ${webhook.label}.` });
    } catch (cause) {
      setWebhookNote({
        ok: false,
        message: cause instanceof ApiError ? cause.message : 'Testet kunde inte skickas.',
      });
    } finally {
      setWebhookBusy(undefined);
    }
  };

  const unchanged =
    state &&
    state.active &&
    selected.size === state.areas.length &&
    state.areas.every((area) => selected.has(area)) &&
    places.size === (state.locations ?? []).length &&
    (state.locations ?? []).every((name) => places.has(name)) &&
    includeWithoutLocation === (state.includeWithoutLocation ?? false) &&
    includeWithoutTechnologies === (state.includeWithoutTechnologies ?? false) &&
    emailEnabled === (state.emailEnabled ?? true) &&
    notifyEmail.trim().toLowerCase() === (state.notifyEmail ?? state.email).toLowerCase() &&
    [...selected].every(sameTechs);

  return (
    <section className="panel" style={{ maxWidth: 780 }}>
      <div className="panel__header">
        <h2 className="panel__title">Bevaka nya uppdrag</h2>
        <span className="panel__tools">{state?.email}</span>
      </div>
      <p className="panel__subtitle">
        Välj de områden du vill följa. Insamlingen kör var trettionde minut mellan 07 och 20, och så fort ett nytt
        uppdrag dyker upp inom något av dina områden får du ett mejl – oftast inom en halvtimme efter att annonsen
        publicerats.
        Bevakningen gäller framåt: du får bara uppdrag som dyker upp efter att du sparat den, aldrig det som redan
        ligger i listan. Händer inget kommer inget mejl. Kryssar du ur allt stängs bevakningen av.
      </p>

      <div className="subscribe-scope">
        <strong>Bevakningen gäller samtliga källor.</strong> Område, teknik och ort är det du väljer här – filter på
        källa, år eller teknik i de andra flikarna påverkar aldrig vad du får i mejlen, och siffrorna nedan räknas på
        all data.
        {sources.length > 0 ? (
          <>
            {' '}
            Just nu bevakas {sources.length} källor: {sources.map((source) => source.name).join(', ')}. Tillkommer en ny
            källa ingår den automatiskt.
          </>
        ) : null}
      </div>

      {error ? (
        <div className="notice">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </div>
      ) : null}

      {state && !state.active && state.areas.length > 0 ? (
        <div className="notice">
          <span aria-hidden="true">⚠</span>
          <span>
            Din bevakning är avstängd – en administratör kan ha stoppat utskicken. Dina val finns kvar; spara om för
            att starta den igen.
          </span>
        </div>
      ) : null}

      <div className="subscribe-grid">
        {areas.map((area) => {
          const checked = selected.has(area.id);
          return (
            <label key={area.id} className={`subscribe-item${checked ? ' subscribe-item--on' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(area.id)} />
              <span className="subscribe-item__name">{area.name}</span>
              <span className="subscribe-item__count">{countFor(area.id)} uppdrag</span>
            </label>
          );
        })}
      </div>

      {selected.size > 0 ? (
        <>
          <div className="panel__header" style={{ marginTop: 26 }}>
            <h3 className="panel__title" style={{ fontSize: 15 }}>
              Tekniker per område
            </h3>
          </div>
          <p className="panel__subtitle">
            Vill du bara ha vissa tekniker inom ett område väljer du dem här. Lämnar du ett område tomt får du allt
            inom det området – det räcker att uppdraget nämner en av de valda teknikerna.
          </p>

          {areas
            .filter((area) => selected.has(area.id))
            .map((area) => (
              <AreaTechPicker
                key={area.id}
                areaName={area.name}
                chosen={techs[area.id] ?? []}
                options={stats.techByArea[area.id] ?? []}
                allTechnologies={allTechnologies}
                onToggle={(tech) => toggleTech(area.id, tech)}
                onClear={() => clearTechs(area.id)}
              />
            ))}

          {narrowedAreas > 0 ? (
            <label className="watch-toggle">
              <input
                type="checkbox"
                checked={includeWithoutTechnologies}
                onChange={(event) => {
                  setSaved(false);
                  setIncludeWithoutTechnologies(event.target.checked);
                }}
              />
              <span>
                Ta med uppdrag där källan inte anger några tekniker.{' '}
                <span className="panel__tools">
                  Flera källor publicerar bara titel och datum – Developers Bay har tekniker på 16 av 62 annonser.
                  Utan det här valet försvinner de ur bevakningen så fort du valt en teknik.
                </span>
              </span>
            </label>
          ) : null}
        </>
      ) : null}

      <div className="panel__header" style={{ marginTop: 26 }}>
        <h3 className="panel__title" style={{ fontSize: 15 }}>
          Orter
        </h3>
        {places.size > 0 ? (
          <button type="button" className="link-button" onClick={() => { setSaved(false); setPlaces(new Set()); }}>
            Rensa orter
          </button>
        ) : null}
      </div>
      <p className="panel__subtitle">
        {places.size === 0
          ? 'Alla orter ingår. Välj en eller flera om du bara vill ha uppdrag därifrån.'
          : `Bara uppdrag i ${[...places].join(', ')}.`}{' '}
        Väljer du ett land räknas varje ort i det landet in – Sverige tar med både Linköping och annonser som bara
        säger "Sverige".
      </p>

      {places.size > 0 ? (
        <label className="watch-toggle">
          <input
            type="checkbox"
            checked={includeWithoutLocation}
            onChange={(event) => {
              setSaved(false);
              setIncludeWithoutLocation(event.target.checked);
            }}
          />
          <span>
            Ta med uppdrag där källan inte anger någon ort.{' '}
            <span className="panel__tools">
              Developers Bay anger ort på 1 av 62 annonser och låser resten bakom medlemskap. Utan det här valet
              försvinner hela den källan ur bevakningen så fort du valt en ort.
            </span>
          </span>
        </label>
      ) : null}

      <div className="place-grid">
        {locationOptions.map((place) => {
          const on = places.has(place.name);
          return (
            <button
              key={place.name}
              type="button"
              className={`tag tag--tech${on ? ' tag--on' : ''}`}
              aria-pressed={on}
              onClick={() => togglePlace(place.name)}
            >
              {COUNTRIES.has(place.name) ? `${place.name} (hela landet)` : place.name}{' '}
              <span className="tag__count">{place.count}</span>
            </button>
          );
        })}
      </div>

      <div className="panel__header" style={{ marginTop: 28 }}>
        <h3 className="panel__title" style={{ fontSize: 15 }}>
          Var vill du få plingen?
        </h3>
        {!emailEnabled && webhooks.length === 0 ? (
          <span className="panel__tools" style={{ color: 'var(--status-warning)' }}>
            Ingen kanal vald – du får ingenting
          </span>
        ) : null}
      </div>
      <p className="panel__subtitle">
        Valen ovan styr <em>vad</em> du bevakas på. Här väljer du <em>var</em> det landar. Mejl och Teams är
        oberoende av varandra – du kan ha båda, eller bara den ena.
      </p>

      <div className="channel">
        <div className="channel__head">
          <MailIcon />
          <strong>E-post</strong>
          <label className="channel__toggle">
            <input
              type="checkbox"
              checked={emailEnabled}
              onChange={(event) => {
                setSaved(false);
                setEmailEnabled(event.target.checked);
              }}
            />
            <span>{emailEnabled ? 'På' : 'Av'}</span>
          </label>
        </div>
        <p className="channel__body">
          {emailEnabled ? (
            state?.lastSentAt ? `Senaste utskicket: ${formatDateTime(state.lastSentAt)}.` : 'Inget utskick ännu.'
          ) : (
            'Avstängt – inga mejl skickas till dig, oavsett hur många uppdrag som dyker upp.'
          )}
        </p>
        {emailEnabled ? (
          <label className="auth__field" style={{ marginTop: 8 }}>
            <span>Skicka mejlen till</span>
            <input
              className="control"
              type="email"
              autoComplete="off"
              value={notifyEmail}
              onChange={(event) => {
                setSaved(false);
                setNotifyEmail(event.target.value);
              }}
            />
            <small className="auth__hint">Ändra om du vill ha mejlen på en annan adress än den du är inloggad med.</small>
          </label>
        ) : null}
      </div>

      <div className="channel">
        <div className="channel__head">
          <ChatIcon />
          <strong>Teams och Discord</strong>
          <span className="panel__tools">
            {webhooks.length === 0
              ? 'ingen kanal kopplad'
              : `${webhooks.length} ${webhooks.length === 1 ? 'kanal' : 'kanaler'}`}
          </span>
          <button type="button" className="link-button" onClick={() => setShowGuide((open) => !open)}>
            {showGuide ? 'Dölj hjälpen' : 'Hur hittar jag adressen?'}
          </button>
        </div>
        <p className="channel__body">
          Klistra in en webhook från Teams eller Discord – vi ser själva vilken sort det är. Varje kanal har sin
          egen brytpunkt: en kanal du lägger till i dag får bara det som dyker upp framöver, och ett stopp i
          mejlkvoten tystar den inte. En webhook kan bara skriva i kanalen – den läser ingenting.
        </p>

        {showGuide ? (
          <div className="webhook-help">
            <div className="webhook-help__tabs">
              <button
                type="button"
                className="chip"
                aria-pressed={guideFor === 'teams'}
                onClick={() => setGuideFor('teams')}
              >
                Teams
              </button>
              <button
                type="button"
                className="chip"
                aria-pressed={guideFor === 'discord'}
                onClick={() => setGuideFor('discord')}
              >
                Discord
              </button>
            </div>

            {guideFor === 'teams' ? (
              <ol className="webhook-guide">
                <li>
                  Öppna <strong>Teams</strong> och håll muspekaren över kanalen du vill ha plingen i. Klicka på{' '}
                  <strong>…</strong> och välj <strong>Arbetsflöden</strong> (<em>Workflows</em> på engelska).
                </li>
                <li>
                  Sök upp mallen <strong>Post to a channel when a webhook request is received</strong> och välj den.
                </li>
                <li>
                  Logga in om du blir ombedd, klicka <strong>Nästa</strong>, kontrollera att rätt team och kanal står
                  valda och klicka <strong>Lägg till arbetsflöde</strong>.
                </li>
                <li>
                  Kopiera adressen som visas. <strong>Den visas bara en gång</strong> – stänger du rutan får du skapa
                  flödet på nytt.
                </li>
                <li>Klistra in adressen här nedanför, ge kanalen ett namn och klicka Lägg till kanal.</li>
              </ol>
            ) : (
              <ol className="webhook-guide">
                <li>
                  Öppna <strong>Discord</strong> och håll muspekaren över kanalen du vill ha plingen i. Klicka på
                  kugghjulet, <strong>Redigera kanal</strong>.
                </li>
                <li>
                  Välj <strong>Integrationer</strong> i menyn till vänster och sedan <strong>Webhooks</strong>.
                </li>
                <li>
                  Klicka <strong>Ny webhook</strong>. Du kan döpa den och välja kanal direkt i rutan som dyker upp.
                </li>
                <li>
                  Klicka <strong>Kopiera webhook-URL</strong>. Till skillnad från Teams går den att hämta igen senare,
                  men behandla den som ett lösenord – den som har den kan skriva i kanalen.
                </li>
                <li>Klistra in adressen här nedanför, ge kanalen ett namn och klicka Lägg till kanal.</li>
              </ol>
            )}
          </div>
        ) : null}

        {webhooks.length > 0 ? (
          <ul className="webhook-list">
            {webhooks.map((webhook) => (
              <li key={webhook.id}>
                <div>
                  <strong>{webhook.label}</strong>
                  <span className="tag" style={{ marginLeft: 6 }}>
                    {webhook.kind === 'discord' ? 'Discord' : 'Teams'}
                  </span>
                  <div className="panel__tools">
                    {webhook.host} · tillagd {formatDateTime(webhook.addedAt)}
                    {webhook.lastSentAt
                      ? ` · senaste plinget ${formatDateTime(webhook.lastSentAt)}`
                      : ' · inget pling än'}
                  </div>
                </div>
                <div className="webhook-list__actions">
                  <button
                    type="button"
                    className="chip"
                    disabled={webhookBusy === webhook.id}
                    onClick={() => testChannel(webhook)}
                  >
                    {webhookBusy === webhook.id ? '…' : 'Testpling'}
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    disabled={webhookBusy === webhook.id}
                    onClick={() => removeChannel(webhook)}
                  >
                    Ta bort
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="webhook-add">
          <input
            className="control"
            type="url"
            autoComplete="off"
            placeholder="Klistra in webhook-adressen från Teams eller Discord…"
            value={webhookUrl}
            onChange={(event) => setWebhookUrl(event.target.value)}
          />
          <input
            className="control"
            type="text"
            autoComplete="off"
            placeholder="Namn, t.ex. Säljgruppen"
            value={webhookLabel}
            onChange={(event) => setWebhookLabel(event.target.value)}
          />
          <button
            type="button"
            className="chip"
            disabled={!webhookUrl.trim() || webhookBusy === 'add'}
            onClick={addChannel}
          >
            {webhookBusy === 'add' ? 'Lägger till…' : 'Lägg till kanal'}
          </button>
        </div>

        {webhookNote ? (
          <p className={webhookNote.ok ? 'subscribe-status' : 'panel__tools'} style={{ marginTop: 8 }}>
            {webhookNote.message}
          </p>
        ) : null}
      </div>

      <div className="subscribe-actions">
        <button type="button" className="auth__submit" style={{ width: 'auto' }} disabled={saving || unchanged} onClick={save}>
          {saving ? 'Sparar…' : 'Spara bevakning'}
        </button>
        {saved ? <span className="subscribe-status">Sparat.</span> : null}
        {selected.size === 0 ? (
          <span className="panel__tools">Inget område valt – bevakningen är avstängd.</span>
        ) : (
          <span className="panel__tools">
            {selected.size} {selected.size === 1 ? 'område' : 'områden'} bevakas
            {places.size === 0 ? ', alla orter' : `, ${places.size} ${places.size === 1 ? 'ort' : 'orter'}`}
            {narrowedAreas === 0
              ? ', alla tekniker'
              : `, teknikval i ${narrowedAreas} ${narrowedAreas === 1 ? 'område' : 'områden'}`}
            {' → '}
            {deliverySummary(emailEnabled, webhooks.length)}.
          </span>
        )}
      </div>
    </section>
  );
}
